import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as Highcharts from 'highcharts';
import HighchartsSankey from 'highcharts/modules/sankey';
import HighchartsOrganization from 'highcharts/modules/organization';
import HighchartsVariablePie from 'highcharts/modules/variable-pie';

// Inicializar módulos en orden (sankey es requerido por organization)
HighchartsSankey(Highcharts);
HighchartsOrganization(Highcharts);
HighchartsVariablePie(Highcharts);

// Porcentaje con coma decimal y espacio antes del signo ("97,0 %").
const fmtPct = (v: number): string =>
  `${(v ?? 0).toLocaleString('es-AR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
import {
  IQueryParam,
  ILuminaria,
  ICliente,
  IDispositivoAlarma,
  IActivo,
  ISirena,
  IPopulate,
  IReporteBase,
  IFilter,
  IResumenDatosBase,
} from 'modelos/src';
import { SeriesOptionsType } from 'highcharts';
import { Subscription, firstValueFrom } from 'rxjs';
import { ResumenesDatosService } from '../../../auxiliares/servicios/http/resumenes-datos.service';
import { ultimaComunicacionAlarma } from '../../../auxiliares/comunicadores-alarma.util';
import { ChartColors } from '../../../auxiliares/servicios/chart-colors.service';
import {
  IPieChartData,
  IBarChartData,
  IHeatmapChartData,
  ILineChartData,
} from '../../../auxiliares/graficos/graficos.component';
import { MapaFacilData } from '../../../auxiliares/mapa-facil/interfaces';
import {
  HelperService,
  Modulo,
} from '../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../auxiliares/servicios/lista2.service';
import { IColumnas } from '../../../auxiliares/tabla/tabla.interfaces';
import { StatCardConfig } from '../../../componentes/stat-card/stat-card.component';
import { ChartCardConfig } from '../../../componentes/chart-card/chart-card.component';
import { LoginService } from '../../login/login.service';
import { LuminariasService } from '../luminarias/service';
import { escaparHtml } from '../../../auxiliares/html-seguro.util';

interface GraficoConfig {
  id: string;
  titulo: string;
  tipo:
    | 'pie'
    | 'donut'
    | 'bar'
    | 'column'
    | 'line'
    | 'area'
    | 'heatmap'
    | 'heatmapChart'
    | 'tabla';
  altura?: string;
  // Propiedades específicas se acceden dinámicamente desde el componente
}

interface EstadosCarga {
  luminarias: boolean;
  reportes24h: boolean;
  resumenMensual: boolean;
  cortesSemana: boolean;
  ultimosUplinks: boolean;
  allUplinks: boolean;
  tasaEntrega: boolean;
}

interface CorteDetallado {
  idLuminaria: string;
  nombreLuminaria: string;
  cantidadCortes: number;
  horasCortada: number;
  horaUltimoCorte?: Date;
}

interface IClienteExtend extends ICliente {
  cantidadLuminarias?: number;
  cantidadActivos?: number;
  cantidadAlarmas?: number;
  cantidadSirenas?: number;
  cantidadVehiculos?: number;
  cantidadColectivos?: number;
  // Contadores solo de dispositivos directos (sin descendientes)
  cantidadLuminariasDirectas?: number;
  cantidadActivosDirectos?: number;
  cantidadAlarmasDirectas?: number;
  cantidadSirenasDirectas?: number;
  cantidadVehiculosDirectos?: number;
  cantidadColectivosDirectos?: number;
}

interface IActivoConReporte extends IActivo {
  ultimoReporte?: IReporteBase<'Tracker 4G'>;
}

interface IColectivoConReporte extends IActivo {
  ultimoReporte?: IReporteBase<'Tracker 4G'>;
}

interface IActivoNoVehiculo extends IActivo {
  ultimoReporte?: IReporteBase<any>;
}

type SeccionKey =
  | 'alarmas'
  | 'activos'
  | 'sirenas'
  | 'vehiculos'
  | 'colectivos'
  | 'luminarias';

// Sección del dashboard habilitada según el módulo del permiso activo.
// Única fuente de verdad para qué se fetchea y qué se muestra en el template.
interface SeccionDashboard {
  key: SeccionKey;
  modulo: Modulo;
  fetch: () => Promise<void>;
  necesitaUltimosReportes?: boolean;
  postCarga?: () => void | Promise<void>;
}

@Component({
  selector: 'app-dashboard-general',
  templateUrl: './dashboard-general.component.html',
  styleUrl: './dashboard-general.component.scss',
  standalone: false,
})
export class DashboardGeneralComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  matDialog = inject(MatDialog);
  luminariasService = inject(LuminariasService);
  lista2 = inject(Lista2Service);
  private resumenesDatosService = inject(ResumenesDatosService);

  public statCardClientes?: StatCardConfig;
  public statCardClientesMayoristas?: StatCardConfig;
  public statCardClientesMinoristas?: StatCardConfig;
  public statCardClientesFinales?: StatCardConfig;
  public statCardVehiculos?: StatCardConfig;
  public statCardVehiculosReportando?: StatCardConfig;
  public statCardVehiculosEnMovimiento?: StatCardConfig;
  public statCardActivos?: StatCardConfig;
  public statCardActivosReportando?: StatCardConfig;
  public statCardAlarmas?: StatCardConfig;
  public statCardAlarmasConAlta?: StatCardConfig;
  public statCardAlarmasReportando?: StatCardConfig;
  public statCardColectivos?: StatCardConfig;
  public statCardColectivosConRecorrido?: StatCardConfig;
  public statCardColectivosEnMovimiento?: StatCardConfig;
  public statCardSirenas?: StatCardConfig;
  public statCardSirenasOnline?: StatCardConfig;
  public statCardSirenasOffline?: StatCardConfig;
  public statCardLuminarias?: StatCardConfig;
  public statCardLuminariasReportesRecientes?: StatCardConfig;
  public cargandoStatCards: boolean = true;
  public luminarias: ILuminaria[] = [];
  public alarmas: IDispositivoAlarma[] = [];
  public activos: IActivoNoVehiculo[] = [];
  public sirenas: ISirena[] = [];
  public vehiculos: IActivoConReporte[] = [];
  public colectivos: IColectivoConReporte[] = [];
  public datosListosParaGraficos: boolean = false;
  public datosDispositivosPorMayorista: {
    [idCliente: string]: IPieChartData[];
  } = {};

  // Control de navegación de gráficos
  public vistaActual: 'jerarquia' | 'dispositivos' = 'jerarquia';
  public clienteSeleccionado?: IClienteExtend;
  public clientes: ICliente[] = [];
  public hayJerarquia: boolean = true;

  // Highcharts para gráficos (solo necesitamos la referencia para los módulos)
  private Highcharts: typeof Highcharts = Highcharts;
  public highchartsPlain: typeof Highcharts = Highcharts;

  // Configuraciones para ChartCard
  public chartCardDispositivosTotal?: ChartCardConfig;
  public chartCardJerarquia?: ChartCardConfig;
  public chartCardDispositivosCliente?: ChartCardConfig;
  public chartCardDispositivosClienteDirecto?: ChartCardConfig;
  public chartCardAlarmasPorMarca?: ChartCardConfig;
  public chartCardVehiculosPorTipo?: ChartCardConfig;
  public chartCardVehiculosPorFuncion?: ChartCardConfig;
  public chartCardConsumo?: ChartCardConfig;

  private contadoresDispositivos = {
    luminarias: 0,
    alarmas: 0,
    activos: 0,
    sirenas: 0,
    vehiculos: 0,
    colectivos: 0,
  };
  // Datos detallados de cortes por luminaria
  public cortesDetallados: CorteDetallado[] = [];

  // Configuración de columnas para tabla de cortes
  public columnasCortesDetallados: IColumnas<CorteDetallado>[] = [
    {
      header: { label: 'Luminaria', sort: 'nombreLuminaria' },
      row: { field: 'nombreLuminaria' },
    },
    {
      header: { label: 'Cantidad de cortes', sort: 'cantidadCortes' },
      row: { field: 'cantidadCortes' },
    },
    {
      header: { label: 'Tiempo cortada', sort: 'horasCortada' },
      row: {
        parse: (dato: CorteDetallado) => {
          const horas = Math.floor(dato.horasCortada);
          const minutos = Math.floor((dato.horasCortada - horas) * 60);
          return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`;
        },
      },
    },
    {
      header: { label: 'Último corte' },
      row: {
        field: 'horaUltimoCorte',
        pipe: { pipe: DatePipe, args: 'dd/MM/yyyy HH:mm' },
      },
    },
  ];

  // Datos para gráficos
  public datosEstados: IPieChartData[] = [];
  public datosEncendido: IPieChartData[] = [];
  public datosModelos: IPieChartData[] = [];
  public datosDispositivos: IPieChartData[] = [];
  public datosModos: IPieChartData[] = [];
  public datosDataRate: IPieChartData[] = [];

  ///
  public clientesMayoristas: IClienteExtend[] = [];
  public esClienteMinorista: boolean = false;
  ///

  // Datos para gráficos de barras/columnas
  public datosConsumo: IBarChartData[] = [];
  public categoriasConsumo: string[] = [];
  public datosTiempoEncendido: IBarChartData[] = [];
  public categoriasTiempoEncendido: string[] = [];

  // Datos para heatmap de canales (frecuencias por fecha)
  public datosCanalesHeatmap?: IHeatmapChartData;
  public categoriasCanalesFechas: string[] = [];
  public categoriasCanalesFrecuencias: string[] = [];

  // Datos para gráficos de líneas
  public datosCortes: ILineChartData[] = [];
  public categoriasCortes: string[] = [];

  // Datos para heatmap geográfico de cortes
  public datosHeatmapCortes: MapaFacilData = {};

  // Configuración de gráficos ordenables
  public graficos: GraficoConfig[] = [];

  //Propiedades de carga
  public cargando: EstadosCarga = {
    luminarias: true,
    reportes24h: true,
    resumenMensual: true,
    cortesSemana: true,
    ultimosUplinks: true,
    allUplinks: true,
    tasaEntrega: true,
  };
  private alarmas$: Subscription;
  private modelosAlarmas$: Subscription;
  private luminarias$: Subscription;
  private vehiculos$: Subscription;
  private colectivos$: Subscription;
  private activos$: Subscription;
  private sirenas$: Subscription;
  private clientes$: Subscription;

  // Registro de secciones: qué módulo habilita cada una, su fetch y sus
  // builders posteriores. El orden replica el orden de los sub-tabs.
  private readonly secciones: SeccionDashboard[] = [
    {
      key: 'alarmas',
      modulo: 'Alarmas',
      fetch: () => this.listarAlarmas(),
      postCarga: () => this.crearGraficoAlarmasPorMarca(),
    },
    {
      key: 'activos',
      modulo: 'Activos',
      fetch: () => this.listarActivos(),
      necesitaUltimosReportes: true,
      postCarga: () => this.actualizarStatCardsActivos(),
    },
    {
      key: 'sirenas',
      modulo: 'Sirenas',
      fetch: () => this.listarSirenas(),
    },
    {
      key: 'vehiculos',
      modulo: 'Vehículos',
      fetch: () => this.listarVehiculos(),
      necesitaUltimosReportes: true,
      postCarga: async () => {
        this.actualizarStatCardsVehiculos();
        this.crearGraficosVehiculos();
        await this.generarChartConsumo();
      },
    },
    {
      key: 'colectivos',
      modulo: 'Colectivos',
      fetch: () => this.listarColectivos(),
      necesitaUltimosReportes: true,
      postCarga: () => this.actualizarStatCardsColectivos(),
    },
    {
      key: 'luminarias',
      modulo: 'Luminarias',
      fetch: () => this.listarLuminarias(),
    },
  ];

  public puedeVer: Record<SeccionKey, boolean> = {
    alarmas: false,
    activos: false,
    sirenas: false,
    vehiculos: false,
    colectivos: false,
    luminarias: false,
  };
  public hayModulosDispositivos: boolean = false;

  // Listados
  public listarLuminarias(): Promise<void> {
    return new Promise((resolve) => {
      this.cargando.luminarias = true;
      const query: IQueryParam = {
        sort: 'identificacion',
        page: 0,
        includeChildren: true,
        select: `identificacion idCliente idsAncestros fechaUltimaComunicacion`,
      };
      this.luminarias$?.unsubscribe();
      this.luminarias$ = this.lista2.luminarias(query, 0).subscribe({
        next: (data) => {
          this.luminarias = data.datos;
          // Actualizar statCard creando un nuevo array completo
          this.actualizarStatCard(
            'Luminarias registradas',
            this.luminarias.length,
          );
          this.actualizarStatCardsLuminarias();
          resolve();
        },
        error: (error) => {
          console.error('Error al listar luminarias:', error);
          this.cargando.luminarias = false;
          resolve();
        },
      });
    });
  }

  public listarAlarmas(): Promise<void> {
    return new Promise((resolve) => {
      const populate: IPopulate[] = [{ path: 'modelo', select: 'marca' }];
      const query: IQueryParam = {
        populate: JSON.stringify(populate),
        sort: 'identificacion',
        page: 0,
        includeChildren: true,
        unlimited: true,
        // La última comunicación está en cada comunicador
        // (ultimaComunicacionAlarma); el fechaUltimaComunicacion de la alarma
        // ya no se actualiza y dejaba "Alarmas comunicando" en 0
        select: `identificacion idCliente idsAncestros comunicadores.fechaUltimaComunicacion idModelo fechaAlta`,
      };
      this.alarmas$?.unsubscribe();
      this.alarmas$ = this.lista2.alarmas(query, 0).subscribe({
        next: (data) => {
          this.alarmas = data.datos;
          this.actualizarStatCard('Alarmas registradas', data.datos.length);
          this.actualizarStatCardsAlarmas();
          resolve();
        },
        error: (error) => {
          console.error('Error al listar alarmas:', error);
          resolve();
        },
      });
    });
  }

  public listarClientes(): Promise<void> {
    return new Promise((resolve) => {
      const query: IQueryParam = {
        sort: 'nombre',
        page: 0,
        includeChildren: true,
        unlimited: true,
        select: `nombre tipoCliente idPadre`,
      };

      this.clientes$?.unsubscribe();
      this.clientes$ = this.lista2.clientes(query, 0).subscribe({
        next: (data) => {
          // Guardar todos los clientes
          this.clientes = data.datos;
          const clientePropio = LoginService.getClientePropio();

          // Filtrar clientes excluyendo el cliente propio
          const clientesSinPropio = data.datos.filter(
            (c) => c._id !== clientePropio?._id,
          );

          const totalClientes = clientesSinPropio.length;
          this.clientesMayoristas = data.datos.filter(
            (c) => c.tipoCliente === 'Mayorista',
          );
          const mayoristas = clientesSinPropio.filter(
            (c) => c.tipoCliente === 'Mayorista',
          ).length;
          const minoristas = clientesSinPropio.filter(
            (c) => c.tipoCliente === 'Minorista',
          ).length;
          const finales = clientesSinPropio.filter(
            (c) => c.tipoCliente === 'Final',
          ).length;

          this.statCardClientes = {
            title: 'Clientes registrados',
            icon: 'business',
            value: totalClientes,
          };

          this.statCardClientesMayoristas = {
            title: 'Clientes mayoristas',
            icon: 'store',
            value: mayoristas,
            color: 'primary',
          };

          this.statCardClientesMinoristas = {
            title: 'Clientes minoristas',
            icon: 'storefront',
            value: minoristas,
            color: 'primary',
          };

          this.statCardClientesFinales = {
            title: 'Clientes finales',
            icon: 'business',
            value: finales,
            color: 'primary',
          };

          resolve();
        },
        error: (error) => {
          console.error('Error al listar clientes:', error);
          resolve();
        },
      });
    });
  }

  public listarActivos(): Promise<void> {
    return new Promise((resolve) => {
      const query: IQueryParam = {
        sort: 'identificacion',
        page: 0,
        includeChildren: true,
        unlimited: true,
        select: `_id identificacion idCliente idsAncestros idTracker categoria`,
        populate: JSON.stringify([
          {
            path: 'tracker',
            select: 'frecReporte tipo',
          },
        ]),
      };
      this.activos$?.unsubscribe();
      this.activos$ = this.lista2.activos(query, 0).subscribe({
        next: async (data) => {
          // Filtrar solo activos con tracker que NO son vehículos
          this.activos = data.datos.filter(
            (a) => a.idTracker && a.categoria !== 'Vehículo',
          );

          // Actualizar conteo
          this.actualizarStatCard('Activos registrados', this.activos.length);
          resolve();
        },
        error: (error) => {
          console.error('Error al listar activos:', error);
          resolve();
        },
      });
    });
  }

  public listarSirenas(): Promise<void> {
    return new Promise((resolve) => {
      const query: IQueryParam = {
        sort: 'identificacion',
        page: 0,
        includeChildren: true,
        unlimited: true,
        select: `identificacion idCliente idsAncestros idExterno chipId estado`,
      };
      this.sirenas$?.unsubscribe();
      this.sirenas$ = this.lista2.sirenasG(query, 0).subscribe({
        next: async (data) => {
          // Cargar las sirenasG primero
          const sirenasG = data.datos;

          // Ahora cargar las sirenas de seguridad con su estado online
          const querySirenas: IQueryParam = {
            includeChildren: true,
            unlimited: true,
            select: 'chipId online fechaOnline fechaOffline',
          };

          try {
            const sirenasData = await firstValueFrom(
              this.lista2.sirenas(querySirenas, 0),
            );

            // Indexar sirenas de seguridad por _id y chipId para join eficiente
            const sirenasById = new Map<string, any>();
            const sirenasByChipId = new Map<string, any>();
            for (const s of sirenasData.datos) {
              if ((s as any)._id) sirenasById.set((s as any)._id, s);
              if ((s as any).chipId) sirenasByChipId.set((s as any).chipId, s);
            }

            // Asociar cada sirenaG con su sirena correspondiente (por idExterno o chipId)
            this.sirenas = sirenasG.map((sirenaG) => {
              const sirena =
                (sirenaG.idExterno
                  ? sirenasById.get(sirenaG.idExterno)
                  : undefined) ||
                (sirenaG.chipId
                  ? sirenasByChipId.get(sirenaG.chipId)
                  : undefined);
              return {
                ...sirenaG,
                sirena: sirena,
              } as any;
            });

            this.actualizarStatCard('Sirenas registradas', this.sirenas.length);
            this.actualizarStatCardsSirenas();
          } catch (error) {
            console.error('Error al cargar sirenas:', error);
            this.sirenas = sirenasG as any;
            this.actualizarStatCard('Sirenas registradas', this.sirenas.length);
          }

          resolve();
        },
        error: (error) => {
          console.error('Error al listar sirenas:', error);
          resolve();
        },
      });
    });
  }

  public listarVehiculos(): Promise<void> {
    return new Promise((resolve) => {
      const query: IQueryParam = {
        sort: 'patente',
        page: 0,
        includeChildren: true,
        unlimited: true,
        select: `_id patente idCliente idsAncestros funcion vehiculo idTracker`,
        populate: JSON.stringify([
          {
            path: 'tracker',
            select: 'frecReporte',
          },
        ]),
      };
      this.vehiculos$?.unsubscribe();
      this.vehiculos$ = this.lista2.vehiculos(query, 0).subscribe({
        next: async (data) => {
          this.vehiculos = data.datos;

          // Actualizar conteo
          this.actualizarStatCard(
            'Vehículos registrados',
            this.vehiculos.length,
          );

          resolve();
        },
        error: (error) => {
          console.error('Error al listar vehículos:', error);
          resolve();
        },
      });
    });
  }

  public listarColectivos(): Promise<void> {
    return new Promise((resolve) => {
      const query: IQueryParam = {
        sort: 'interno',
        page: 0,
        includeChildren: true,
        unlimited: true,
        select: `_id interno idCliente idsAncestros vehiculo.idRecorrido idTracker`,
        populate: JSON.stringify([
          {
            path: 'tracker',
            select: 'frecReporte',
          },
        ]),
      };
      this.colectivos$?.unsubscribe();
      this.colectivos$ = this.lista2.colectivos(query, 0).subscribe({
        next: async (data) => {
          this.colectivos = data.datos;

          // Actualizar conteo
          this.actualizarStatCard(
            'Colectivos registrados',
            this.colectivos.length,
          );

          resolve();
        },
        error: (error) => {
          console.error('Error al listar colectivos:', error);
          resolve();
        },
      });
    });
  }

  private actualizarStatCard(titulo: string, valor: number) {
    // Actualizar contadores para el gráfico
    if (titulo === 'Luminarias registradas') {
      this.contadoresDispositivos.luminarias = valor;
    } else if (titulo === 'Alarmas registradas') {
      this.contadoresDispositivos.alarmas = valor;
    } else if (titulo === 'Activos registrados') {
      this.contadoresDispositivos.activos = valor;
    } else if (titulo === 'Sirenas registradas') {
      this.contadoresDispositivos.sirenas = valor;
    } else if (titulo === 'Vehículos registrados') {
      this.contadoresDispositivos.vehiculos = valor;
    } else if (titulo === 'Colectivos registrados') {
      this.contadoresDispositivos.colectivos = valor;
    }

    // Actualizar el gráfico de Highcharts solo si los datos están listos
    if (this.datosListosParaGraficos) {
      this.actualizarGraficoDispositivos();
    }
  }

  private actualizarGraficoDispositivos(): void {
    const datos = [
      {
        name: 'Luminarias',
        y: this.contadoresDispositivos.luminarias,
        color: '#FFA726',
      },
      {
        name: 'Alarmas',
        y: this.contadoresDispositivos.alarmas,
        color: '#EF5350',
      },
      {
        name: 'Activos',
        y: this.contadoresDispositivos.activos,
        color: '#42A5F5',
      },
      {
        name: 'Sirenas',
        y: this.contadoresDispositivos.sirenas,
        color: '#AB47BC',
      },
      {
        name: 'Vehículos',
        y: this.contadoresDispositivos.vehiculos,
        color: '#66BB6A',
      },
      {
        name: 'Colectivos',
        y: this.contadoresDispositivos.colectivos,
        color: '#26C6DA',
      },
    ].filter((item) => item.y > 0);

    const totalDispositivos = datos.reduce((sum, item) => sum + item.y, 0);

    this.chartCardDispositivosTotal = {
      title: 'Distribución de dispositivos',
      icon: 'pie_chart',
      variante: 'panel',
      chartHeight: '400px',
      sinDatos: datos.length ? null : 'No hay dispositivos registrados',
      chartOptions: {
        chart: {
          type: 'pie',
        },
        title: {
          text: `Total<br/><strong>${totalDispositivos.toLocaleString('es-AR')}</strong>`,
          align: 'center',
          verticalAlign: 'middle',
          y: 15,
          style: {
            fontSize: '16px',
          },
        },
        tooltip: {
          pointFormatter: function (this: any) {
            return `${this.series.name}: <b>${this.y.toLocaleString('es-AR')}</b> (${fmtPct(this.percentage)})`;
          },
        },
        plotOptions: {
          pie: {
            innerSize: '75%',
            dataLabels: {
              enabled: true,
              formatter: function (this: any) {
                return `<b>${escaparHtml(this.point.name)}</b><br>${fmtPct(this.percentage)}`;
              },
              distance: 10,
            },
          },
        },
        series: [
          {
            name: 'Cantidad',
            type: 'pie',
            data: datos,
          },
        ],
      },
    };
  }

  private construirDatosJerarquia(): {
    data: [string, string][];
    nodes: any[];
  } {
    const data: [string, string][] = [];
    const nodes: any[] = [];

    const clientePropio = LoginService.getClientePropio();

    // Función para obtener todos los descendientes de un cliente
    const obtenerDescendientes = (clienteId: string): string[] => {
      const descendientes: string[] = [];
      const hijosDirectos = this.clientes.filter(
        (c) => c.idPadre === clienteId,
      );

      hijosDirectos.forEach((hijo) => {
        descendientes.push(hijo._id!);
        descendientes.push(...obtenerDescendientes(hijo._id!));
      });

      return descendientes;
    };

    // Filtrar clientes visibles según el cliente del usuario
    let clientesVisibles: ICliente[] = [];
    let clienteRaiz: ICliente | undefined;

    if (clientePropio) {
      // El usuario solo puede ver su cliente (si es mayorista) y sus descendientes mayoristas
      clienteRaiz = clientePropio;
      const idsDescendientes = obtenerDescendientes(clientePropio._id!);

      // Incluir el cliente propio directamente si es mayorista
      if (clientePropio.tipoCliente === 'Mayorista') {
        clientesVisibles.push(clientePropio);
      }

      // Agregar descendientes que sean mayoristas
      const descendientesMayoristas = this.clientes.filter(
        (c) =>
          idsDescendientes.includes(c._id!) && c.tipoCliente === 'Mayorista',
      );
      clientesVisibles.push(...descendientesMayoristas);

      // Si el cliente propio no es mayorista, no mostrar nada
      if (clientePropio.tipoCliente !== 'Mayorista') {
        return { data, nodes };
      }
    } else {
      // Sin cliente propio (admin), puede ver todos los mayoristas
      clientesVisibles = this.clientes.filter(
        (c) => c.tipoCliente === 'Mayorista',
      );
      // Buscar el primer cliente mayorista sin padre como raíz (si existe)
      clienteRaiz = clientesVisibles.find((c) => !c.idPadre);
    }

    if (clientesVisibles.length === 0) {
      return { data, nodes };
    }

    // Función recursiva para calcular heredados acumulativos
    // Usa this.clientes para contar TODOS los tipos de clientes (mayoristas, minoristas, finales)
    const calcularHeredados = (
      clienteId: string,
    ): { minoristas: number; finales: number } => {
      // Obtener hijos directos de este cliente (de TODOS los clientes, no solo mayoristas)
      const hijosDirectos = this.clientes.filter(
        (c) => c.idPadre === clienteId,
      );

      let totalMinoristas = 0;
      let totalFinales = 0;

      hijosDirectos.forEach((hijo) => {
        // Contar al hijo si es minorista o final
        if (hijo.tipoCliente === 'Minorista') {
          totalMinoristas++;
        } else if (hijo.tipoCliente === 'Final') {
          totalFinales++;
        }

        // Recursivamente sumar los heredados de cada hijo
        const heredadosDelHijo = calcularHeredados(hijo._id!);
        totalMinoristas += heredadosDelHijo.minoristas;
        totalFinales += heredadosDelHijo.finales;
      });

      return { minoristas: totalMinoristas, finales: totalFinales };
    };

    // Construir mapa de padre -> hijos visibles para el recorrido DFS
    const clientesVisiblesSet = new Set(clientesVisibles.map((c) => c._id));
    const hijosPorPadre = new Map<string, ICliente[]>();
    clientesVisibles.forEach((cliente) => {
      if (
        cliente.idPadre &&
        clientesVisiblesSet.has(cliente.idPadre) &&
        cliente._id !== clientePropio?._id
      ) {
        if (!hijosPorPadre.has(cliente.idPadre)) {
          hijosPorPadre.set(cliente.idPadre, []);
        }
        hijosPorPadre.get(cliente.idPadre)!.push(cliente);
      }
    });

    // Función auxiliar para construir el nodo y sus datos
    const construirNodo = (cliente: ICliente): void => {
      const hijosDirectos = this.clientes.filter(
        (c) => c.idPadre === cliente._id,
      );

      const hijosMinoristasDirectos = hijosDirectos.filter(
        (c) => c.tipoCliente === 'Minorista',
      ).length;
      const hijosFinalesDirectos = hijosDirectos.filter(
        (c) => c.tipoCliente === 'Final',
      ).length;

      const heredados = calcularHeredados(cliente._id!);
      const hijosMinoristasHeredados =
        heredados.minoristas - hijosMinoristasDirectos;
      const hijosFinalesHeredados = heredados.finales - hijosFinalesDirectos;

      nodes.push({
        id: cliente._id,
        name: cliente.nombre,
        hijosMinoristasDirectos,
        hijosFinalesDirectos,
        hijosMinoristasHeredados,
        hijosFinalesHeredados,
      });
    };

    // Recorrido DFS desde cada raíz para garantizar que los hijos de un mismo
    // padre queden consecutivos en el array data (sin cruce de ramas)
    const visitado = new Set<string>();
    const dfs = (clienteId: string, padreId?: string): void => {
      if (visitado.has(clienteId)) return;
      visitado.add(clienteId);

      const cliente = clientesVisibles.find((c) => c._id === clienteId);
      if (!cliente) return;

      construirNodo(cliente);

      // Agregar enlace padre -> hijo (excepto si es el nodo raíz)
      if (padreId) {
        data.push([padreId, clienteId]);
      }

      // Recorrer hijos en el orden que vienen (ya filtrados y mapeados)
      const hijos = hijosPorPadre.get(clienteId) ?? [];
      hijos.forEach((hijo) => dfs(hijo._id!, clienteId));
    };

    // Identificar raíces: clientes visibles sin padre visible
    const raices = clientesVisibles.filter(
      (c) =>
        !c.idPadre ||
        !clientesVisiblesSet.has(c.idPadre) ||
        c._id === clientePropio?._id,
    );
    raices.forEach((raiz) => dfs(raiz._id!));

    // Por si quedó algún nodo desconectado (no debería)
    clientesVisibles.forEach((c) => {
      if (!visitado.has(c._id!)) dfs(c._id!);
    });

    return { data, nodes };
  }

  private crearGraficoJerarquia(): void {
    const { data, nodes } = this.construirDatosJerarquia();

    if (nodes.length === 0) {
      // Sin clientes: el panel muestra el motivo en lugar de quedar cargando
      this.chartCardJerarquia = {
        title: 'Jerarquía de clientes',
        icon: 'account_tree',
        variante: 'panel',
        chartHeight: '600px',
        sinDatos: 'No hay clientes para mostrar en la jerarquía',
      };
      return;
    }

    // Si solo hay un nodo sin relaciones, mostrar directamente la vista de dispositivos
    if (data.length === 0 && nodes.length === 1) {
      this.hayJerarquia = false;
      const clientePropio = LoginService.getClientePropio();
      if (clientePropio) {
        this.mostrarDispositivosCliente(clientePropio._id!);
      }
      return;
    }

    // Si llegamos aquí, hay jerarquía disponible
    this.hayJerarquia = true;

    this.chartCardJerarquia = {
      title: 'Jerarquía de clientes',
      icon: 'account_tree',
      variante: 'panel',
      chartHeight: '600px',
      chartOptions: {
        chart: {
          height: 600,
          inverted: true,
          type: 'organization',
        },
        title: {
          text: '',
        },
        accessibility: {
          point: {
            descriptionFormat:
              '{add index 1}. {toNode.name}, reports to {fromNode.name}',
          },
        },
        series: [
          {
            type: 'organization',
            name: 'Clientes',
            keys: ['from', 'to'],
            data: data,
            hangingIndent: 20,
            minNodeLength: 50,
            linkRadius: 10,
            levels: [
              {
                level: 0,
                color: '#AB47BC',
                dataLabels: {
                  color: 'white',
                },
                height: 25,
              },
              {
                level: 1,
                color: '#FF9800',
                dataLabels: {
                  color: 'white',
                },
                height: 25,
              },
              {
                level: 2,
                color: '#66BB6A',
                dataLabels: {
                  color: 'white',
                },
                height: 25,
              },
            ],
            nodes: nodes,
            colorByPoint: false,
            color: '#42A5F5',
            dataLabels: {
              color: 'white',
            },
            borderColor: 'var(--bg-grafico)',
            nodeWidth: 'auto',
            cursor: 'pointer',
            point: {
              events: {
                click: (event: any) => {
                  event.preventDefault();
                  const idCliente = event.point.id;
                  setTimeout(() => {
                    this.mostrarDispositivosCliente(idCliente);
                  }, 0);
                },
              },
            },
          } as any,
        ],
        tooltip: {
          outside: true,
          useHTML: true,
          formatter: function (this: any) {
            const point = this.point;
            let html = `<b>${escaparHtml(point.name)}</b><br/>`;

            const tieneInfo =
              point.hijosMinoristasDirectos !== undefined ||
              point.hijosFinalesDirectos !== undefined ||
              point.hijosMinoristasHeredados !== undefined ||
              point.hijosFinalesHeredados !== undefined;

            if (tieneInfo) {
              // Mostrar minoristas
              if (
                point.hijosMinoristasDirectos > 0 ||
                point.hijosMinoristasHeredados > 0
              ) {
                html += '<br/><b>Minoristas:</b><br/>';
                if (point.hijosMinoristasDirectos > 0) {
                  html += `&nbsp;&nbsp;Directos: ${point.hijosMinoristasDirectos}<br/>`;
                }
                if (point.hijosMinoristasHeredados > 0) {
                  html += `&nbsp;&nbsp;Heredados: ${point.hijosMinoristasHeredados}<br/>`;
                }
              }

              // Mostrar finales
              if (
                point.hijosFinalesDirectos > 0 ||
                point.hijosFinalesHeredados > 0
              ) {
                html += '<br/><b>Finales:</b><br/>';
                if (point.hijosFinalesDirectos > 0) {
                  html += `&nbsp;&nbsp;Directos: ${point.hijosFinalesDirectos}<br/>`;
                }
                if (point.hijosFinalesHeredados > 0) {
                  html += `&nbsp;&nbsp;Heredados: ${point.hijosFinalesHeredados}<br/>`;
                }
              }

              // Si no tiene ninguno
              if (
                point.hijosMinoristasDirectos === 0 &&
                point.hijosFinalesDirectos === 0 &&
                point.hijosMinoristasHeredados === 0 &&
                point.hijosFinalesHeredados === 0
              ) {
                html += '<br/>Sin clientes';
              }
            }

            return html;
          },
        },
        exporting: {
          enabled: false,
        },
      },
    };
  }

  private mostrarDispositivosCliente(idCliente: string): void {
    const cliente = this.clientes.find((c) => c._id === idCliente);
    if (!cliente) {
      return;
    }

    // Solo mostrar dispositivos para clientes mayoristas
    if (cliente.tipoCliente !== 'Mayorista') {
      return;
    }

    // Cambiar la vista
    this.clienteSeleccionado = cliente as IClienteExtend;
    this.vistaActual = 'dispositivos';

    // Calcular y crear los gráficos inmediatamente
    this.calcularDispositivosCliente(this.clienteSeleccionado);
    this.crearGraficoDispositivosCliente();
    this.crearGraficoDispositivosClienteDirecto();
  }

  public volverAJerarquia(): void {
    // Limpiar las configuraciones de los gráficos de dispositivos del cliente
    this.chartCardDispositivosCliente = undefined;
    this.chartCardDispositivosClienteDirecto = undefined;

    this.vistaActual = 'jerarquia';
    this.clienteSeleccionado = undefined;
  }

  private crearGraficoAlarmasPorMarca(): void {
    // Agrupar alarmas por marca usando el mismo criterio que los stat cards (< 1 hora)
    const alarmasPorMarca: {
      [marca: string]: { conComunicacion: number; sinComunicacion: number };
    } = {};

    const ahora = Date.now();
    const unaHora = 60 * 60 * 1000; // 1 hora en milisegundos

    this.alarmas.forEach((alarma) => {
      const marca = alarma.modelo?.marca || 'Sin marca';

      if (!alarmasPorMarca[marca]) {
        alarmasPorMarca[marca] = { conComunicacion: 0, sinComunicacion: 0 };
      }

      // Usar el mismo criterio que statCardAlarmasReportando (< 1 hora)
      const ultimaComunicacion = ultimaComunicacionAlarma(alarma);
      if (
        ultimaComunicacion &&
        ahora - new Date(ultimaComunicacion).getTime() < unaHora
      ) {
        alarmasPorMarca[marca].conComunicacion++;
      } else {
        alarmasPorMarca[marca].sinComunicacion++;
      }
    });

    // Preparar datos para el gráfico
    const marcas = Object.keys(alarmasPorMarca).sort();
    const datosConComunicacion = marcas.map(
      (marca) => alarmasPorMarca[marca].conComunicacion,
    );
    const datosSinComunicacion = marcas.map(
      (marca) => alarmasPorMarca[marca].sinComunicacion,
    );

    this.chartCardAlarmasPorMarca = {
      title: 'Alarmas por marca',
      icon: 'bar_chart',
      variante: 'panel',
      chartHeight: '500px',
      sinDatos: marcas.length ? null : 'No hay alarmas para mostrar por marca',
      chartOptions: {
        chart: {
          type: 'column',
        },
        title: {
          text: undefined,
        },
        xAxis: {
          categories: marcas,
          title: {
            text: 'Marca',
          },
        },
        yAxis: {
          min: 0,
          allowDecimals: false,
          title: {
            text: 'Cantidad de Alarmas',
          },
          stackLabels: {
            enabled: true,
          },
        },
        tooltip: {
          headerFormat: '<b>{category}</b><br/>',
          pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}',
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            borderRadius: 4,
            dataLabels: {
              enabled: false,
            },
          },
        },
        series: [
          {
            name: 'Con Comunicación',
            type: 'column',
            data: datosConComunicacion,
            color: '#66BB6A',
          },
          {
            name: 'Sin Comunicación',
            type: 'column',
            data: datosSinComunicacion,
            color: '#EF5350',
          },
        ],
        legend: {
          enabled: true,
          align: 'right',
          verticalAlign: 'middle',
          layout: 'vertical',
        },
        exporting: {
          enabled: false,
        },
      },
    };
  }

  private async completarTodosLosUltimosReportes(): Promise<void> {
    const query: IQueryParam = {
      sort: '-fechaCreacion',
      includeChildren: true,
      select: 'idsAsignados valores.velocidad fechaCreacion tipoReporte',
    };

    try {
      const data = await firstValueFrom(
        this.lista2.ultimosReportesGenericos<any>(query),
      );

      for (const vehiculo of this.vehiculos) {
        const reporte = data.datos.find((r) =>
          r.idsAsignados?.includes(vehiculo._id!),
        );
        if (reporte) vehiculo.ultimoReporte = reporte;
      }

      for (const activo of this.activos) {
        const reporte = data.datos.find((r) =>
          r.idsAsignados?.includes(activo._id!),
        );
        if (reporte) activo.ultimoReporte = reporte;
      }

      for (const colectivo of this.colectivos) {
        const reporte = data.datos.find((r) =>
          r.idsAsignados?.includes(colectivo._id!),
        );
        if (reporte) colectivo.ultimoReporte = reporte;
      }
    } catch (error) {
      console.error('Error al obtener últimos reportes:', error);
    }
  }

  private actualizarStatCardsVehiculos(): void {
    // Stat card de vehículos totales
    this.statCardVehiculos = {
      title: 'Vehículos registrados',
      icon: 'directions_car',
      value: this.vehiculos.length,
      color: 'primary',
    };

    // Stat card de vehículos reportando (reporte < 1 hora)
    const unaHoraEnMs = 60 * 60 * 1000;
    const ahora = new Date().getTime();

    const vehiculosReportando = this.vehiculos.filter((v) => {
      if (!v.ultimoReporte?.fechaCreacion) return false;

      const tiempoReporte = new Date(v.ultimoReporte.fechaCreacion).getTime();
      const diferencia = ahora - tiempoReporte;

      return diferencia < unaHoraEnMs;
    }).length;

    this.statCardVehiculosReportando = {
      title: 'Vehículos reportando',
      icon: 'navigation',
      value: vehiculosReportando,
      color: 'success',
      subtitle: `${vehiculosReportando} de ${this.vehiculos.length} (< 1h)`,
    };

    // Stat card de vehículos en movimiento (velocidad > 5)
    const vehiculosEnMovimiento = this.vehiculos.filter(
      (v) =>
        v.ultimoReporte?.valores?.velocidad &&
        v.ultimoReporte.valores.velocidad > 5,
    ).length;

    this.statCardVehiculosEnMovimiento = {
      title: 'Vehículos en movimiento',
      icon: 'speed',
      value: vehiculosEnMovimiento,
      color: 'info',
      subtitle: `${vehiculosEnMovimiento} de ${this.vehiculos.length}`,
    };
  }

  private actualizarStatCardsActivos(): void {
    // Stat card de activos totales con tracker (no vehículos)
    this.statCardActivos = {
      title: 'Activos con Tracker',
      icon: 'pin_drop',
      value: this.activos.length,
      color: 'primary',
    };

    // Stat card de activos reportando (reporte < 6 horas)
    const seisHorasEnMs = 6 * 60 * 60 * 1000;
    const ahora = new Date().getTime();

    const activosReportando = this.activos.filter((a) => {
      if (!a.ultimoReporte?.fechaCreacion) return false;

      const tiempoReporte = new Date(a.ultimoReporte.fechaCreacion).getTime();
      const diferencia = ahora - tiempoReporte;

      return diferencia < seisHorasEnMs;
    }).length;

    this.statCardActivosReportando = {
      title: 'Activos reportando',
      icon: 'my_location',
      value: activosReportando,
      color: 'success',
      subtitle: `${activosReportando} de ${this.activos.length} (< 6hs)`,
    };
  }

  private actualizarStatCardsAlarmas(): void {
    // Stat card de alarmas totales
    this.statCardAlarmas = {
      title: 'Alarmas registradas',
      icon: 'doorbell',
      value: this.alarmas.length,
      color: 'primary',
    };

    // Stat card de alarmas con fecha de alta
    const alarmasConAlta = this.alarmas.filter((a) => a.fechaAlta).length;

    this.statCardAlarmasConAlta = {
      title: 'Alarmas con alta',
      icon: 'check_circle',
      value: alarmasConAlta,
      color: 'info',
      subtitle: `${alarmasConAlta} de ${this.alarmas.length}`,
    };

    // Stat card de alarmas reportando (reporte < 1 hora)
    const unaHoraEnMs = 60 * 60 * 1000;
    const ahora = new Date().getTime();

    const alarmasReportando = this.alarmas.filter((a) => {
      const ultimaComunicacion = ultimaComunicacionAlarma(a);
      if (!ultimaComunicacion) return false;

      const tiempoReporte = new Date(ultimaComunicacion).getTime();
      const diferencia = ahora - tiempoReporte;

      return diferencia < unaHoraEnMs;
    }).length;

    this.statCardAlarmasReportando = {
      title: 'Alarmas comunicando',
      icon: 'cell_tower',
      value: alarmasReportando,
      color: 'success',
      subtitle: `${alarmasReportando} de ${this.alarmas.length} (< 1h)`,
    };
  }

  private actualizarStatCardsLuminarias(): void {
    // Stat card de luminarias totales
    this.statCardLuminarias = {
      title: 'Luminarias registradas',
      icon: 'lightbulb',
      value: this.luminarias.length,
      color: 'primary',
    };

    // Stat card de luminarias con reportes recientes (< 24 horas)
    const unDiaEnMs = 24 * 60 * 60 * 1000;
    const ahora = new Date().getTime();

    const luminariasConReportesRecientes = this.luminarias.filter((l) => {
      if (!l.fechaUltimaComunicacion) return false;

      const tiempoReporte = new Date(l.fechaUltimaComunicacion).getTime();
      const diferencia = ahora - tiempoReporte;

      return diferencia < unDiaEnMs;
    }).length;

    this.statCardLuminariasReportesRecientes = {
      title: 'Reportes recientes',
      icon: 'access_time',
      value: luminariasConReportesRecientes,
      color: 'success',
      subtitle: `${luminariasConReportesRecientes} de ${this.luminarias.length} (últimas 24hs)`,
    };
  }

  private actualizarStatCardsColectivos(): void {
    // Stat card de colectivos totales
    this.statCardColectivos = {
      title: 'Colectivos registrados',
      icon: 'directions_bus',
      value: this.colectivos.length,
      color: 'primary',
    };

    // Stat card de colectivos con recorrido asignado
    const colectivosConRecorrido = this.colectivos.filter(
      (c) => c.vehiculo?.idRecorrido,
    ).length;

    this.statCardColectivosConRecorrido = {
      title: 'Con recorrido asignado',
      icon: 'route',
      value: colectivosConRecorrido,
      color: 'info',
      subtitle: `${colectivosConRecorrido} de ${this.colectivos.length}`,
    };

    // Stat card de colectivos con recorrido asignado y en movimiento (velocidad > 5)
    const colectivosEnMovimiento = this.colectivos.filter(
      (c) =>
        c.vehiculo?.idRecorrido &&
        c.ultimoReporte?.valores?.velocidad !== undefined &&
        c.ultimoReporte.valores.velocidad > 5,
    ).length;

    this.statCardColectivosEnMovimiento = {
      title: 'En recorrido y movimiento',
      icon: 'speed',
      value: colectivosEnMovimiento,
      color: 'success',
      subtitle: `${colectivosEnMovimiento} de ${colectivosConRecorrido}`,
    };
  }

  private actualizarStatCardsSirenas(): void {
    // Stat card de sirenas totales
    this.statCardSirenas = {
      title: 'Sirenas registradas',
      icon: 'campaign',
      value: this.sirenas.length,
      color: 'primary',
    };

    // Stat card de sirenas online
    const sirenasOnline = this.sirenas.filter(
      (s) => s.estado === 'Online',
    ).length;

    this.statCardSirenasOnline = {
      title: 'Sirenas online',
      icon: 'check_circle',
      value: sirenasOnline,
      color: 'success',
      subtitle: `${sirenasOnline} de ${this.sirenas.length}`,
    };

    // Stat card de sirenas offline
    const sirenasOffline = this.sirenas.filter(
      (s) => s.estado === 'Offline',
    ).length;

    this.statCardSirenasOffline = {
      title: 'Sirenas offline',
      icon: 'cloud_off',
      value: sirenasOffline,
      color: 'error',
      subtitle: `${sirenasOffline} de ${this.sirenas.length}`,
    };
  }

  private crearGraficosVehiculos(): void {
    // Gráfico por tipo de vehículo
    const vehiculosPorTipo: {
      [tipo: string]: number;
    } = {};

    this.vehiculos.forEach((vehiculo) => {
      const tipo = vehiculo.vehiculo?.tipo || 'Sin tipo';
      vehiculosPorTipo[tipo] = (vehiculosPorTipo[tipo] || 0) + 1;
    });

    const datosVehiculosTipo = Object.keys(vehiculosPorTipo)
      .sort()
      .map((tipo) => ({
        name: tipo,
        y: vehiculosPorTipo[tipo],
      }));

    this.chartCardVehiculosPorTipo = {
      title: 'Vehículos por tipo',
      icon: 'directions_car',
      variante: 'panel',
      chartHeight: '500px',
      sinDatos: datosVehiculosTipo.length
        ? null
        : 'No hay vehículos para mostrar por tipo',
      chartOptions: {
        chart: {
          type: 'pie',
          zooming: {
            type: 'xy',
          },
          panning: {
            enabled: true,
            type: 'xy',
          },
          panKey: 'shift',
        },
        title: {
          text: undefined,
        },
        tooltip: {
          pointFormatter: function (this: any) {
            return `${this.series.name}: <b>${fmtPct(this.percentage)}</b>`;
          },
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            borderRadius: 5,
            dataLabels: {
              enabled: true,
              formatter: function (this: any) {
                return `${this.point.name}: ${fmtPct(this.percentage)}`;
              },
              style: {
                fontSize: '1em',
              },
            },
            showInLegend: true,
          },
        },
        series: [
          {
            name: 'Porcentaje',
            type: 'pie',
            colorByPoint: true,
            data: datosVehiculosTipo,
          },
        ],
        legend: {
          enabled: true,
        },
        exporting: {
          enabled: false,
        },
      } as any,
    };

    // Gráfico por función del vehículo
    const vehiculosPorFuncion: {
      [funcion: string]: number;
    } = {};

    this.vehiculos.forEach((vehiculo) => {
      const funcion = vehiculo.funcion || 'Sin función';
      vehiculosPorFuncion[funcion] = (vehiculosPorFuncion[funcion] || 0) + 1;
    });

    const datosVehiculosFuncion = Object.keys(vehiculosPorFuncion)
      .sort()
      .map((funcion) => ({
        name: funcion,
        y: vehiculosPorFuncion[funcion],
      }));

    this.chartCardVehiculosPorFuncion = {
      title: 'Vehículos por función',
      icon: 'assignment',
      variante: 'panel',
      chartHeight: '500px',
      sinDatos: datosVehiculosFuncion.length
        ? null
        : 'No hay vehículos para mostrar por función',
      chartOptions: {
        chart: {
          type: 'pie',
          zooming: {
            type: 'xy',
          },
          panning: {
            enabled: true,
            type: 'xy',
          },
          panKey: 'shift',
        },
        title: {
          text: undefined,
        },
        tooltip: {
          pointFormatter: function (this: any) {
            return `${this.series.name}: <b>${fmtPct(this.percentage)}</b>`;
          },
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            borderRadius: 5,
            dataLabels: {
              enabled: true,
              formatter: function (this: any) {
                return `${this.point.name}: ${fmtPct(this.percentage)}`;
              },
              style: {
                fontSize: '1em',
              },
            },
            showInLegend: true,
          },
        },
        series: [
          {
            name: 'Porcentaje',
            type: 'pie',
            colorByPoint: true,
            data: datosVehiculosFuncion,
          },
        ],
        legend: {
          enabled: true,
        },
        exporting: {
          enabled: false,
        },
      } as any,
    };
  }

  private async generarChartConsumo(): Promise<void> {
    try {
      const filterResumen: IFilter<
        IResumenDatosBase<'Consumo Mensual Combustible Vehículos'>
      > = {
        tipo: 'Consumo Mensual Combustible Vehículos',
        agrupacion: 'Cliente',
        agrupacionTiempo: 'Mensual',
      };

      const queryResumen: IQueryParam = {
        filter: JSON.stringify(filterResumen),
        sort: 'periodoInicio',
        includeChildren: true,
        unlimited: true,
        select: 'periodoInicio resumen',
      };

      const resultado =
        await this.resumenesDatosService.getFiltered<'Consumo Mensual Combustible Vehículos'>(
          queryResumen,
        );

      const porMes: Record<
        string,
        {
          kmRecorridos: number;
          consumoEstimado: number;
          consumoDeclarado: number;
        }
      > = {};

      resultado.datos.forEach((d) => {
        if (!d.periodoInicio) return;
        const fecha = new Date(d.periodoInicio);
        const key = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
        if (!porMes[key]) {
          porMes[key] = {
            kmRecorridos: 0,
            consumoEstimado: 0,
            consumoDeclarado: 0,
          };
        }
        porMes[key].kmRecorridos += d.resumen?.kmRecorridos || 0;
        porMes[key].consumoEstimado += d.resumen?.consumoEstimado || 0;
        porMes[key].consumoDeclarado += d.resumen?.consumoDeclarado || 0;
      });

      const keysOrdenadas = Object.keys(porMes).sort().slice(-3);

      while (keysOrdenadas.length < 3) {
        const primero = keysOrdenadas[0];
        let anio: number, mes: number;
        if (primero) {
          [anio, mes] = primero.split('-').map(Number);
          mes -= 1;
          if (mes < 1) {
            mes = 12;
            anio -= 1;
          }
        } else {
          const hoy = new Date();
          anio = hoy.getFullYear();
          mes = hoy.getMonth() + 1;
        }
        const key = `${anio}-${String(mes).padStart(2, '0')}`;
        keysOrdenadas.unshift(key);
        porMes[key] = {
          kmRecorridos: 0,
          consumoEstimado: 0,
          consumoDeclarado: 0,
        };
      }

      const monthNames = [
        'Ene',
        'Feb',
        'Mar',
        'Abr',
        'May',
        'Jun',
        'Jul',
        'Ago',
        'Sep',
        'Oct',
        'Nov',
        'Dic',
      ];

      const categorias = keysOrdenadas.map((key) => {
        const [anio, mes] = key.split('-').map(Number);
        return `${monthNames[mes - 1]}/${anio}`;
      });

      const datosKm = keysOrdenadas.map((key) =>
        Math.round(porMes[key].kmRecorridos),
      );
      const datosConsumoEstimado = keysOrdenadas.map(
        (key) => Math.round(porMes[key].consumoEstimado * 10) / 10,
      );
      const datosConsumoDeclarado = keysOrdenadas.map(
        (key) => Math.round(porMes[key].consumoDeclarado * 10) / 10,
      );

      const series: SeriesOptionsType[] = [
        {
          type: 'column',
          name: 'Km Recorridos',
          data: datosKm,
          yAxis: 0,
          color: ChartColors.color1(),
          tooltip: { valueSuffix: ' km' },
        },
        {
          type: 'column',
          name: 'Consumo Estimado',
          data: datosConsumoEstimado,
          yAxis: 1,
          color: ChartColors.color2(),
          tooltip: { valueSuffix: ' L' },
        },
        {
          type: 'column',
          name: 'Consumo Declarado',
          data: datosConsumoDeclarado,
          yAxis: 1,
          color: ChartColors.color3(),
          tooltip: { valueSuffix: ' L' },
        },
      ];

      // Sin km ni litros en los 3 meses: mensaje en lugar de columnas vacías
      const hayConsumo = [
        ...datosKm,
        ...datosConsumoEstimado,
        ...datosConsumoDeclarado,
      ].some((v) => v > 0);

      this.chartCardConsumo = {
        title: 'Consumo de combustible (últimos 3 meses)',
        icon: 'local_gas_station',
        variante: 'panel',
        chartHeight: '350px',
        sinDatos: hayConsumo
          ? null
          : 'No hay consumo de combustible registrado en los últimos 3 meses',
        chartOptions: {
          chart: { type: 'column' },
          xAxis: {
            categories: categorias,
            title: { text: 'Mes/Año' },
          },
          yAxis: [
            {
              title: { text: 'Kilómetros recorridos' },
              labels: { format: '{value} km' },
            },
            {
              title: { text: 'Consumo (L)' },
              labels: { format: '{value} L' },
              opposite: true,
            },
          ],
          legend: { enabled: true },
          plotOptions: {
            column: {
              grouping: true,
              pointPadding: 0.1,
              borderWidth: 0,
            },
          },
          series,
        } as any,
      };
    } catch (error) {
      console.error('Error al generar gráfico de consumo:', error);
      // El panel queda visible con el motivo, en lugar de quedar cargando
      this.chartCardConsumo = {
        title: 'Consumo de combustible (últimos 3 meses)',
        icon: 'local_gas_station',
        variante: 'panel',
        chartHeight: '350px',
        sinDatos: 'No se pudo cargar el consumo de combustible',
      };
    }
  }

  private calcularDispositivosCliente(cliente: IClienteExtend): void {
    const idCliente = cliente._id;

    // Contar dispositivos del cliente (incluyendo descendientes)
    cliente.cantidadLuminarias = this.luminarias.filter(
      (l) =>
        l.idCliente === idCliente ||
        (l.idsAncestros && l.idsAncestros.includes(idCliente)),
    ).length;

    cliente.cantidadAlarmas = this.alarmas.filter(
      (a) =>
        a.idCliente === idCliente ||
        (a.idsAncestros && a.idsAncestros.includes(idCliente)),
    ).length;

    cliente.cantidadActivos = this.activos.filter(
      (a) =>
        a.idCliente === idCliente ||
        (a.idsAncestros && a.idsAncestros.includes(idCliente)),
    ).length;

    cliente.cantidadSirenas = this.sirenas.filter(
      (s) =>
        s.idCliente === idCliente ||
        (s.idsAncestros && s.idsAncestros.includes(idCliente)),
    ).length;

    cliente.cantidadVehiculos = this.vehiculos.filter(
      (v) =>
        v.idCliente === idCliente ||
        (v.idsAncestros && v.idsAncestros.includes(idCliente)),
    ).length;

    cliente.cantidadColectivos = this.colectivos.filter(
      (c) =>
        c.idCliente === idCliente ||
        (c.idsAncestros && c.idsAncestros.includes(idCliente)),
    ).length;

    // Contar solo dispositivos directos (sin descendientes)
    cliente.cantidadLuminariasDirectas = this.luminarias.filter(
      (l) => l.idCliente === idCliente,
    ).length;

    cliente.cantidadAlarmasDirectas = this.alarmas.filter(
      (a) => a.idCliente === idCliente,
    ).length;

    cliente.cantidadActivosDirectos = this.activos.filter(
      (a) => a.idCliente === idCliente,
    ).length;

    cliente.cantidadSirenasDirectas = this.sirenas.filter(
      (s) => s.idCliente === idCliente,
    ).length;

    cliente.cantidadVehiculosDirectos = this.vehiculos.filter(
      (v) => v.idCliente === idCliente,
    ).length;

    cliente.cantidadColectivosDirectos = this.colectivos.filter(
      (c) => c.idCliente === idCliente,
    ).length;
  }

  private crearGraficoDispositivosCliente(): void {
    if (!this.clienteSeleccionado) {
      return;
    }

    const datos = [
      this.clienteSeleccionado.cantidadLuminarias! > 0
        ? {
            name: 'Luminarias',
            y: this.clienteSeleccionado.cantidadLuminarias!,
            color: '#FFA726',
          }
        : null,
      this.clienteSeleccionado.cantidadAlarmas! > 0
        ? {
            name: 'Alarmas',
            y: this.clienteSeleccionado.cantidadAlarmas!,
            color: '#EF5350',
          }
        : null,
      this.clienteSeleccionado.cantidadActivos! > 0
        ? {
            name: 'Activos',
            y: this.clienteSeleccionado.cantidadActivos!,
            color: '#42A5F5',
          }
        : null,
      this.clienteSeleccionado.cantidadSirenas! > 0
        ? {
            name: 'Sirenas',
            y: this.clienteSeleccionado.cantidadSirenas!,
            color: '#AB47BC',
          }
        : null,
      this.clienteSeleccionado.cantidadVehiculos! > 0
        ? {
            name: 'Vehículos',
            y: this.clienteSeleccionado.cantidadVehiculos!,
            color: '#66BB6A',
          }
        : null,
      this.clienteSeleccionado.cantidadColectivos! > 0
        ? {
            name: 'Colectivos',
            y: this.clienteSeleccionado.cantidadColectivos!,
            color: '#26C6DA',
          }
        : null,
    ].filter((item) => item !== null) as IPieChartData[];

    const totalDispositivos = datos.reduce((sum, item) => sum + item.y, 0);

    // Crear la configuración del ChartCard
    this.chartCardDispositivosCliente = {
      title: `Dispositivos en la jerarquía de ${this.clienteSeleccionado.nombre}`,
      icon: 'devices',
      variante: 'panel',
      chartHeight: '500px',
      sinDatos: datos.length
        ? null
        : `No hay dispositivos en la jerarquía de ${this.clienteSeleccionado.nombre}`,
      chartOptions: {
        chart: {
          type: 'pie',
          custom: {},
          events: {
            render() {
              const chart = this as any;
              const series = chart.series[0];
              let customLabel = chart.options.chart.custom.label;

              if (!customLabel) {
                customLabel = chart.options.chart.custom.label = chart.renderer
                  .label(
                    'Total<br/>' +
                      `<strong>${totalDispositivos.toLocaleString('es-AR')}</strong>`,
                  )
                  .css({
                    color: 'var(--text-color)',
                    textAnchor: 'middle',
                  })
                  .add();
              } else {
                customLabel.attr({
                  text:
                    'Total<br/>' +
                    `<strong>${totalDispositivos.toLocaleString('es-AR')}</strong>`,
                });
                customLabel.css({
                  color: 'var(--text-color)',
                });
              }

              const x = series.center[0] + chart.plotLeft;
              const y =
                series.center[1] +
                chart.plotTop -
                customLabel.attr('height') / 2;

              customLabel.attr({ x, y });
              customLabel.css({
                fontSize: `${series.center[2] / 12}px`,
                color: 'var(--text-color)',
              });
            },
          },
        },
        accessibility: {
          point: {
            valueSuffix: '%',
          },
        },
        title: {
          text: undefined,
        },
        tooltip: {
          pointFormatter: function (this: any) {
            return `${this.series.name}: <b>${this.y.toLocaleString('es-AR')}</b> (${fmtPct(this.percentage)})`;
          },
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          series: {
            allowPointSelect: true,
            cursor: 'pointer',
            borderRadius: 8,
            dataLabels: [
              {
                enabled: true,
                distance: 20,
                format: '{point.name}',
                style: {
                  color: 'var(--text-color)',
                  textOutline: 'none',
                },
              },
              {
                enabled: true,
                distance: -15,
                formatter: function (this: any) {
                  return fmtPct(this.percentage);
                },
                style: {
                  fontSize: '0.9em',
                  color: 'var(--text-color)',
                  textOutline: 'none',
                },
              },
            ],
            showInLegend: true,
          },
        },
        series: [
          {
            name: 'Cantidad',
            type: 'pie',
            colorByPoint: true,
            innerSize: '75%',
            data: datos,
          },
        ],
        exporting: {
          enabled: false,
        },
      } as any,
    };
  }

  private crearGraficoDispositivosClienteDirecto(): void {
    if (!this.clienteSeleccionado) {
      return;
    }

    const datos = [
      this.clienteSeleccionado.cantidadLuminariasDirectas! > 0
        ? {
            name: 'Luminarias',
            y: this.clienteSeleccionado.cantidadLuminariasDirectas!,
            color: '#FFA726',
          }
        : null,
      this.clienteSeleccionado.cantidadAlarmasDirectas! > 0
        ? {
            name: 'Alarmas',
            y: this.clienteSeleccionado.cantidadAlarmasDirectas!,
            color: '#EF5350',
          }
        : null,
      this.clienteSeleccionado.cantidadActivosDirectos! > 0
        ? {
            name: 'Activos',
            y: this.clienteSeleccionado.cantidadActivosDirectos!,
            color: '#42A5F5',
          }
        : null,
      this.clienteSeleccionado.cantidadSirenasDirectas! > 0
        ? {
            name: 'Sirenas',
            y: this.clienteSeleccionado.cantidadSirenasDirectas!,
            color: '#AB47BC',
          }
        : null,
      this.clienteSeleccionado.cantidadVehiculosDirectos! > 0
        ? {
            name: 'Vehículos',
            y: this.clienteSeleccionado.cantidadVehiculosDirectos!,
            color: '#66BB6A',
          }
        : null,
      this.clienteSeleccionado.cantidadColectivosDirectos! > 0
        ? {
            name: 'Colectivos',
            y: this.clienteSeleccionado.cantidadColectivosDirectos!,
            color: '#26C6DA',
          }
        : null,
    ].filter((item) => item !== null) as IPieChartData[];

    const totalDispositivos = datos.reduce((sum, item) => sum + item.y, 0);

    // Crear la configuración del ChartCard
    this.chartCardDispositivosClienteDirecto = {
      title: `Dispositivos propios de ${this.clienteSeleccionado.nombre}`,
      icon: 'devices_other',
      variante: 'panel',
      chartHeight: '500px',
      sinDatos: datos.length
        ? null
        : `${this.clienteSeleccionado.nombre} no tiene dispositivos propios`,
      chartOptions: {
        chart: {
          type: 'pie',
          custom: {},
          events: {
            render() {
              const chart = this as any;
              const series = chart.series[0];
              let customLabel = chart.options.chart.custom.label;

              if (!customLabel) {
                customLabel = chart.options.chart.custom.label = chart.renderer
                  .label(
                    'Total<br/>' +
                      `<strong>${totalDispositivos.toLocaleString('es-AR')}</strong>`,
                  )
                  .css({
                    color: 'var(--text-color)',
                    textAnchor: 'middle',
                  })
                  .add();
              } else {
                customLabel.attr({
                  text:
                    'Total<br/>' +
                    `<strong>${totalDispositivos.toLocaleString('es-AR')}</strong>`,
                });
                customLabel.css({
                  color: 'var(--text-color)',
                });
              }

              const x = series.center[0] + chart.plotLeft;
              const y =
                series.center[1] +
                chart.plotTop -
                customLabel.attr('height') / 2;

              customLabel.attr({ x, y });
              customLabel.css({
                fontSize: `${series.center[2] / 12}px`,
                color: 'var(--text-color)',
              });
            },
          },
        },
        accessibility: {
          point: {
            valueSuffix: '%',
          },
        },
        title: {
          text: undefined,
        },
        tooltip: {
          pointFormatter: function (this: any) {
            return `${this.series.name}: <b>${this.y.toLocaleString('es-AR')}</b> (${fmtPct(this.percentage)})`;
          },
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          series: {
            allowPointSelect: true,
            cursor: 'pointer',
            borderRadius: 8,
            dataLabels: [
              {
                enabled: true,
                distance: 20,
                format: '{point.name}',
                style: {
                  color: 'var(--text-color)',
                  textOutline: 'none',
                },
              },
              {
                enabled: true,
                distance: -15,
                formatter: function (this: any) {
                  return fmtPct(this.percentage);
                },
                style: {
                  fontSize: '0.9em',
                  color: 'var(--text-color)',
                  textOutline: 'none',
                },
              },
            ],
            showInLegend: true,
          },
        },
        series: [
          {
            name: 'Cantidad',
            type: 'pie',
            colorByPoint: true,
            innerSize: '75%',
            data: datos,
          },
        ],
        exporting: {
          enabled: false,
        },
      } as any,
    };
  }

  private calcularDispositivosPorMayorista(): void {
    // Por cada cliente mayorista, contar sus dispositivos
    this.clientesMayoristas.forEach((mayorista) => {
      const idMayorista = mayorista._id;

      // Contar luminarias del mayorista
      const cantLuminarias = this.luminarias.filter(
        (l) =>
          l.idCliente === idMayorista ||
          (l.idsAncestros && l.idsAncestros.includes(idMayorista)),
      ).length;

      // Contar alarmas del mayorista
      const cantAlarmas = this.alarmas.filter(
        (a) =>
          a.idCliente === idMayorista ||
          (a.idsAncestros && a.idsAncestros.includes(idMayorista)),
      ).length;

      // Contar activos del mayorista
      const cantActivos = this.activos.filter(
        (a) =>
          a.idCliente === idMayorista ||
          (a.idsAncestros && a.idsAncestros.includes(idMayorista)),
      ).length;

      // Contar sirenas del mayorista
      const cantSirenas = this.sirenas.filter(
        (s) =>
          s.idCliente === idMayorista ||
          (s.idsAncestros && s.idsAncestros.includes(idMayorista)),
      ).length;

      // Contar vehículos del mayorista
      const cantVehiculos = this.vehiculos.filter(
        (v) =>
          v.idCliente === idMayorista ||
          (v.idsAncestros && v.idsAncestros.includes(idMayorista)),
      ).length;

      // Contar colectivos del mayorista
      const cantColectivos = this.colectivos.filter(
        (c) =>
          c.idCliente === idMayorista ||
          (c.idsAncestros && c.idsAncestros.includes(idMayorista)),
      ).length;

      // Guardar en el objeto extendido
      mayorista.cantidadLuminarias = cantLuminarias;
      mayorista.cantidadAlarmas = cantAlarmas;
      mayorista.cantidadActivos = cantActivos;
      mayorista.cantidadSirenas = cantSirenas;
      mayorista.cantidadVehiculos = cantVehiculos;
      mayorista.cantidadColectivos = cantColectivos;

      // Crear datos para el gráfico solo si tiene dispositivos
      const totalDispositivos =
        cantLuminarias +
        cantAlarmas +
        cantActivos +
        cantSirenas +
        cantVehiculos +
        cantColectivos;

      if (totalDispositivos > 0) {
        this.datosDispositivosPorMayorista[idMayorista] = [
          cantLuminarias > 0
            ? { name: 'Luminarias', y: cantLuminarias, color: '#FFA726' }
            : null,
          cantAlarmas > 0
            ? { name: 'Alarmas', y: cantAlarmas, color: '#EF5350' }
            : null,
          cantActivos > 0
            ? { name: 'Activos', y: cantActivos, color: '#42A5F5' }
            : null,
          cantSirenas > 0
            ? { name: 'Sirenas', y: cantSirenas, color: '#AB47BC' }
            : null,
          cantVehiculos > 0
            ? { name: 'Vehículos', y: cantVehiculos, color: '#66BB6A' }
            : null,
          cantColectivos > 0
            ? { name: 'Colectivos', y: cantColectivos, color: '#26C6DA' }
            : null,
        ].filter((item) => item !== null) as IPieChartData[];

        // Agregar gráfico a la configuración
        this.graficos.push({
          id: `mayorista-${idMayorista}`,
          titulo: `Dispositivos - ${mayorista.nombre}`,
          tipo: 'donut',
        });
      }
    });
  }

  async ngOnInit(): Promise<void> {
    // Verificar si el cliente actual es minorista
    const clientePropio = LoginService.getClientePropio();
    this.esClienteMinorista = clientePropio?.tipoCliente === 'Minorista';

    // Armar el dashboard según los módulos del permiso activo
    for (const s of this.secciones) {
      this.puedeVer[s.key] = this.helper.tieneModulo(s.modulo);
    }
    const seccionesActivas = this.secciones.filter((s) => this.puedeVer[s.key]);
    this.hayModulosDispositivos = seccionesActivas.length > 0;

    try {
      // Clientes primero: los mayoristas se necesitan para la jerarquía y
      // los donuts por mayorista, UI que el minorista no ve
      if (!this.esClienteMinorista) {
        await this.listarClientes();
      }

      // Cargar en paralelo solo los dispositivos de módulos habilitados
      await Promise.all(seccionesActivas.map((s) => s.fetch()));

      // Una sola llamada para completar los últimos reportes
      if (seccionesActivas.some((s) => s.necesitaUltimosReportes)) {
        await this.completarTodosLosUltimosReportes();
      }

      // Todos los datos están listos
      this.datosListosParaGraficos = true;

      // Gráficos de clientes (top row / tab Clientes)
      if (!this.esClienteMinorista) {
        this.calcularDispositivosPorMayorista();
        this.crearGraficoJerarquia();
      }

      // Donut total: solo cuenta lo fetcheado
      if (this.hayModulosDispositivos) {
        this.actualizarGraficoDispositivos();
      }

      // Stat cards y gráficos por sección
      for (const s of seccionesActivas) {
        await s.postCarga?.();
      }
    } catch (error) {
      console.error('Error al cargar los datos del dashboard:', error);
    }
  }

  ngOnDestroy(): void {
    this.activos$?.unsubscribe();
    this.alarmas$?.unsubscribe();
    this.clientes$?.unsubscribe();
    this.colectivos$?.unsubscribe();
    this.luminarias$?.unsubscribe();
    this.sirenas$?.unsubscribe();
    this.vehiculos$?.unsubscribe();
  }
}
